import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const Signup = () => {
    const { signup } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        activation_code: "",
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        password: "",
    });
    const [loading, setLoading] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const u = await signup({
                ...form,
                email: form.email.trim(),
                activation_code: form.activation_code.trim().toUpperCase(),
                first_name: form.first_name.trim(),
                last_name: form.last_name.trim(),
                phone: form.phone.trim(),
            });
            toast.success(`Welcome, ${u.first_name || u.name || u.email}`);
            navigate("/dashboard");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Signup failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-72px)] bg-white" data-testid="signup-page">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                <div className="lg:col-span-6">
                    <div className="un-label text-un-grape mb-6">Redeem Your Code</div>
                    <h1 className="un-h1 text-4xl md:text-5xl lg:text-6xl mb-6 leading-[1.05]">
                        You Were <span className="text-un-berry">Invited.</span>
                    </h1>
                    <p className="text-lg text-un-onyx/75 max-w-md mb-6">
                        Welcome to the UnCloned® Short Form Video Strategist. This is the private script creator
                        we&apos;ve reserved for a select group of clients.
                    </p>
                    <p className="text-base text-un-onyx/65 max-w-md mb-10">
                        Enter the activation code from your onboarding email to set up your account and
                        start writing authority-driven scripts in minutes.
                    </p>
                    <div className="un-card p-6 hidden lg:block">
                        <div className="un-label text-un-grape mb-3">Already Have An Account?</div>
                        <Link to="/login" className="un-btn-outline text-sm" data-testid="link-to-login">
                            Sign In
                        </Link>
                    </div>
                </div>

                <div className="lg:col-span-6">
                    <form onSubmit={submit} className="un-card p-8 max-w-lg lg:ml-auto" data-testid="signup-form">
                        <div className="un-label text-un-grape mb-6">Create Client Account</div>

                        <label className="block mb-5">
                            <span className="un-label text-un-onyx block mb-2">Activation Code</span>
                            <input
                                type="text"
                                required
                                value={form.activation_code}
                                onChange={set("activation_code")}
                                className="un-input un-mono uppercase tracking-wider"
                                placeholder="XXXX-XXXX-XXXX"
                                data-testid="signup-code-input"
                            />
                        </label>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                            <label className="block">
                                <span className="un-label text-un-onyx block mb-2">First Name</span>
                                <input
                                    type="text"
                                    required
                                    value={form.first_name}
                                    onChange={set("first_name")}
                                    className="un-input"
                                    placeholder="Jane"
                                    data-testid="signup-first-name-input"
                                    autoComplete="given-name"
                                />
                            </label>
                            <label className="block">
                                <span className="un-label text-un-onyx block mb-2">Last Name</span>
                                <input
                                    type="text"
                                    required
                                    value={form.last_name}
                                    onChange={set("last_name")}
                                    className="un-input"
                                    placeholder="Doe"
                                    data-testid="signup-last-name-input"
                                    autoComplete="family-name"
                                />
                            </label>
                        </div>

                        <label className="block mb-5">
                            <span className="un-label text-un-onyx block mb-2">Email</span>
                            <input
                                type="email"
                                required
                                value={form.email}
                                onChange={set("email")}
                                className="un-input"
                                placeholder="you@brand.com"
                                data-testid="signup-email-input"
                                autoComplete="email"
                            />
                        </label>

                        <label className="block mb-5">
                            <span className="un-label text-un-onyx block mb-2">Phone Number</span>
                            <input
                                type="tel"
                                value={form.phone}
                                onChange={set("phone")}
                                className="un-input"
                                placeholder="(555) 555-0100"
                                data-testid="signup-phone-input"
                                autoComplete="tel"
                            />
                        </label>

                        <label className="block mb-8">
                            <span className="un-label text-un-onyx block mb-2">Password</span>
                            <input
                                type="password"
                                required
                                minLength={8}
                                value={form.password}
                                onChange={set("password")}
                                className="un-input"
                                placeholder="At least 8 characters"
                                data-testid="signup-password-input"
                                autoComplete="new-password"
                            />
                        </label>

                        <button
                            type="submit"
                            disabled={loading}
                            className="un-btn-primary w-full text-base flex items-center justify-center gap-2"
                            data-testid="signup-submit-button"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                            {loading ? "Creating Account…" : "Create Account"}
                        </button>
                        <div className="un-divider mt-8 pt-5 flex items-center justify-between text-sm">
                            <span className="text-un-onyx/60">Already invited?</span>
                            <Link to="/login" className="un-btn-ghost !px-0" data-testid="signup-to-login">
                                Sign In →
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Signup;
